/**
 * Competency Summary Component
 * Displays an expandable list of skills/competencies with feedback and scores
 */

import React from 'react';
import ScoreProgressChart from './ScoreProgressChart';
import { getStudentFeedbackByTag, computeAverageScore, getPostTitle } from './viewUtils';

/**
 * Displays a summary of student competencies with expandable feedback details
 * @param {Object} props - Component props
 * @param {string} props.studentId - The student's user ID
 * @param {Array} props.tagFeedbacks - Array of all tag feedbacks
 * @param {Array} props.posts - Array of all posts
 * @param {Set} props.expandedTags - Set of currently expanded tag names
 * @param {Function} props.toggleTagExpansion - Function to toggle tag expansion
 * @param {Function} props.navigateToPost - Function to navigate to a specific post
 * @param {string} props.noFeedbackMessage - Message to show when no feedback exists
 */
export const CompetencySummary = ({
  studentId,
  tagFeedbacks,
  posts,
  expandedTags,
  toggleTagExpansion,
  navigateToPost,
  noFeedbackMessage = "No feedback yet."
}) => {
  const byTag = getStudentFeedbackByTag(studentId, tagFeedbacks);
  const tagsForStudent = Object.keys(byTag);

  if ((tagsForStudent || []).length === 0) {
    return <p className="no-posts no-posts-padding">{noFeedbackMessage}</p>;
  }

  return (
    <div className="skill-summary-margin">
      {(tagsForStudent || []).map(tag => {
        const items = (byTag[tag] || []).slice().sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        const avg = computeAverageScore(items);
        const scoredCount = items.filter(i => typeof i.score === 'number').length;
        const totalCount = items.length;
        const isExpanded = expandedTags.has(tag);

        return (
          <div key={tag} className="skill-summary-item skill-summary-item-container">
            <div 
              className={`skill-header-container ${isExpanded ? 'expanded' : ''}`}
              onClick={() => toggleTagExpansion(tag)}
              data-skill-tag={tag}
            >
              <div>
                <strong className="skill-tag-title">{tag}</strong>
                <div className="skill-tag-stats">
                  {avg !== null ? `Avg: ${avg} · ` : ''}
                  {`Scored: ${scoredCount} · Entries: ${totalCount}`}
                </div>
              </div>
              <div className={`skill-arrow ${isExpanded ? 'expanded' : ''}`}>
                ▶
              </div>
            </div>
            {isExpanded && (
              <div className="skill-comments skill-comments-container">
                {/* Score Progress Chart */}
                <ScoreProgressChart 
                  feedbackData={items} 
                  skillTag={tag}
                  onPointClick={navigateToPost}
                />
                
                {(items || []).map(tf => (
                  <div 
                    key={tf.id} 
                    className="comment skill-comment-item"
                  >
                    <div className="skill-comment-header">
                      <div>
                        <strong className="skill-comment-teacher">{tf.teacherName}</strong>
                        {typeof tf.score === 'number' && (
                          <span className="skill-comment-score">
                            scored {tf.score}
                          </span>
                        )}
                      </div>
                      <span className="comment-time skill-comment-time">
                        {new Date(tf.timestamp).toLocaleString()}
                      </span>
                    </div>
                    {tf.comment && (
                      <div className="skill-comment-text">
                        "{tf.comment}"
                      </div>
                    )}
                    <div className="skill-comment-link-container">
                      <span 
                        className="post-link"
                        onClick={() => navigateToPost(tf.postId)}
                      >
                        <i className="fa-solid fa-link"></i> {getPostTitle(tf.postId, posts)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

